'use client';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Search,
  Film,
  LogIn,
  UserPlus,
  Home,
  LogOut,
  Settings,
  User,
  Shield
} from 'lucide-react';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import { SearchDialog } from '@/components/SearchDialog';
import { SearchDropdown } from '@/components/SearchDropdown';
import { NotificationBell } from '@/components/NotificationBell';
import { adminService } from '@/lib/admin';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface HeaderProps {
  showSearch?: boolean;
  transparent?: boolean;
  page?: 'landing' | 'dashboard' | 'detail';
}

export function Header({
  showSearch = false,
  transparent = true,
  page = 'landing'
}: HeaderProps) {
  const router = useRouter();
  const { user, userProfile, logout } = useAuth();
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { activeTheme, loading: themeLoading } = useTheme();

  // Determine logo to display (only show after theme is loaded to avoid flickering)
  const logoSrc = activeTheme?.logo?.url || "/image/epify.png";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const adminStatus = await adminService.isUserAdmin(user.uid);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    router.push('/');
  };

  // Use displayName if set, otherwise fall back to username or email
  const displayNameOrUsername = userProfile?.displayName || userProfile?.username || user?.email?.split('@')[0] || 'User';
  // Only calculate initial from userProfile to avoid flash from email initial to displayName initial
  const displayInitial = userProfile ? (userProfile.displayName || userProfile.username || user?.email)?.charAt(0).toUpperCase() || 'U' : '';

  // Dynamic header background - always has gradient for visibility
  // Use CSS variable for top position on mobile only, fixed 0 on desktop
  const headerClasses = isScrolled
    ? "fixed left-0 right-0 z-50 bg-black/95 backdrop-blur-md transition-all duration-300"
    : "fixed left-0 right-0 z-50 bg-gradient-to-b from-black/80 via-black/50 to-transparent backdrop-blur-sm transition-all duration-300";

  // Mobile: use banner height, Desktop: always 0
  const headerStyle = {
    top: 'var(--pwa-banner-height, 0px)'
  } as React.CSSProperties;

  return (
    <>
      <header className={headerClasses} style={headerStyle}>
        <div className="container mx-auto px-4 py-4 md:py-6">
          {/* Mobile Layout */}
          <div className="flex flex-col gap-4 md:hidden">
            {/* Mobile: Logo and Auth buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => router.push('/')}
                  className="transition-opacity hover:opacity-80"
                >
                  <div className="h-6 flex items-center">
                    {!themeLoading && (
                      <Image
                        src={logoSrc}
                        alt="Epify"
                        width={100}
                        height={36}
                        className="h-6 w-auto"
                        priority
                      />
                    )}
                  </div>
                </button>
              </div>

              {/* Mobile Auth Menu */}
              <div className="flex items-center gap-2">
                {user ? (
                  <>
                    <button
                      onClick={() => router.push('/dashboard')}
                      className="flex items-center justify-center h-8 w-8 md:h-10 md:w-10 rounded-full md:backdrop-blur-md md:bg-black/60 md:hover:bg-black/70 transition-all md:shadow-xl md:hover:scale-110 p-0"
                      style={{minWidth: '32px', minHeight: '32px'}}
                    >
                      <Film className="h-5 w-5 text-white" />
                    </button>
                    <NotificationBell />
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center justify-center h-8 w-8 rounded-full transition-all p-0" style={{minWidth: '32px', minHeight: '32px', maxWidth: '32px', maxHeight: '32px'}}>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center overflow-hidden" style={{minWidth: '32px', minHeight: '32px'}}>
                            {userProfile?.photoURL || user?.photoURL ? (
                              <Image
                                src={userProfile?.photoURL || user?.photoURL || ''}
                                alt="Profile"
                                width={32}
                                height={32}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-sm font-bold text-white leading-none">
                                {displayInitial}
                              </span>
                            )}
                          </div>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-72 bg-black/95 backdrop-blur-xl border-2 border-red-600/20 shadow-[0_0_50px_rgba(220,38,38,0.15)] rounded-2xl p-2 overflow-hidden !border-red-600/20"
                        sideOffset={12}
                      >
                        {/* User info header */}
                        <div className="px-3 py-4 mb-2 bg-gradient-to-br from-red-950/30 to-transparent rounded-xl border border-red-900/20">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center ring-4 ring-red-500/20 overflow-hidden">
                              {userProfile?.photoURL || user?.photoURL ? (
                                <Image
                                  src={userProfile?.photoURL || user?.photoURL || ''}
                                  alt="Profile"
                                  width={48}
                                  height={48}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-lg font-bold text-white">
                                  {displayInitial}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              {userProfile?.displayName && userProfile?.username ? (
                                <>
                                  <p className="text-base font-semibold text-white truncate">
                                    {userProfile.displayName}
                                  </p>
                                  <p className="text-xs text-gray-400 truncate">
                                    @{userProfile.username}
                                  </p>
                                </>
                              ) : (
                                <p className="text-base font-semibold text-white truncate">
                                  {userProfile?.username || user?.email?.split('@')[0] || 'User'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Menu items */}
                        <div className="space-y-1">
                          <DropdownMenuItem
                            className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-red-600/20 cursor-pointer transition-all rounded-lg group"
                            onClick={() => router.push('/dashboard')}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-red-600/10 group-hover:bg-red-600/20 flex items-center justify-center transition-colors">
                                <Film className="h-4 w-4 text-red-400" />
                              </div>
                              <span className="font-medium">My Watchlist</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer transition-all rounded-lg group"
                            onClick={() => router.push('/profile')}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-white/5 group-hover:bg-white/10 flex items-center justify-center transition-colors">
                                <User className="h-4 w-4 text-gray-400 group-hover:text-white" />
                              </div>
                              <span className="font-medium">Profile</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer transition-all rounded-lg group"
                            onClick={() => router.push('/settings')}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-white/5 group-hover:bg-white/10 flex items-center justify-center transition-colors">
                                <Settings className="h-4 w-4 text-gray-400 group-hover:text-white" />
                              </div>
                              <span className="font-medium">Settings</span>
                            </div>
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem
                              className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-purple-600/20 cursor-pointer transition-all rounded-lg group"
                              onClick={() => window.open('/admin', '_blank')}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-purple-600/10 group-hover:bg-purple-600/20 flex items-center justify-center transition-colors">
                                  <Shield className="h-4 w-4 text-purple-400" />
                                </div>
                                <span className="font-medium">Admin Panel</span>
                              </div>
                            </DropdownMenuItem>
                          )}
                        </div>

                        <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-2"></div>

                        {/* Logout section */}
                        <DropdownMenuItem
                          className="px-3 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-600/20 cursor-pointer transition-all rounded-lg group"
                          onClick={handleLogout}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-red-600/10 group-hover:bg-red-600/20 flex items-center justify-center transition-colors">
                              <LogOut className="h-4 w-4" />
                            </div>
                            <span className="font-medium">Logout</span>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => router.push('/auth/signin')}
                      className="text-gray-300 hover:text-white hover:bg-white/10 px-3"
                      suppressHydrationWarning
                    >
                      Sign In
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => router.push('/auth/signup')}
                      className="bg-red-600 hover:bg-red-700 text-white px-3"
                      suppressHydrationWarning
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Mobile: Search Bar - Full width */}
            {showSearch && (
              <div className="w-full">
                <SearchDropdown
                  placeholder="Search movies, TV shows, actors, and users..."
                  className="w-full bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-red-500 backdrop-blur-sm"
                />
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            {/* Desktop: Logo */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => router.push('/')}
                className="transition-opacity hover:opacity-80"
              >
                <div className="h-10 flex items-center">
                  {!themeLoading && (
                    <Image
                      src={logoSrc}
                      alt="Epify"
                      width={140}
                      height={48}
                      className="h-10 w-auto"
                      priority
                    />
                  )}
                </div>
              </button>
            </div>

            {/* Desktop: Search Bar - Centered */}
            {showSearch && (
              <div className="flex-1 max-w-xl mx-4">
                <SearchDropdown
                  placeholder="Search movies, TV shows, actors, and users..."
                  className="w-full bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-red-500 backdrop-blur-sm"
                />
              </div>
            )}

            {/* Desktop: Right side content - varies by page */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  {/* My Watchlist - Always show when logged in */}
                  <button
                    onClick={() => router.push('/dashboard')}
                    className="flex items-center justify-center h-10 w-10 rounded-full backdrop-blur-md bg-black/60 hover:bg-black/70 transition-all shadow-xl hover:scale-110 p-0"
                    style={{minWidth: '40px', minHeight: '40px', maxWidth: '40px', maxHeight: '40px'}}
                  >
                    <Film className="h-5 w-5 text-white" />
                  </button>

                  {/* Notification Bell */}
                  <NotificationBell />

                  {/* User Profile with Dropdown */}
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <div className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full backdrop-blur-md bg-black/60 hover:bg-black/70 transition-all shadow-xl cursor-pointer">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center overflow-hidden" style={{minWidth: '32px', minHeight: '32px', maxWidth: '32px', maxHeight: '32px'}}>
                          {userProfile?.photoURL || user?.photoURL ? (
                            <Image
                              src={userProfile?.photoURL || user?.photoURL || ''}
                              alt="Profile"
                              width={32}
                              height={32}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs font-bold text-white leading-none">
                              {displayInitial}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-300 hidden xl:block">
                          {displayNameOrUsername}
                        </span>
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-72 bg-black/95 backdrop-blur-xl border-2 border-red-600/20 shadow-[0_0_50px_rgba(220,38,38,0.15)] rounded-2xl p-2 overflow-hidden !border-red-600/20"
                      sideOffset={12}
                    >
                      {/* User info header */}
                      <div className="px-3 py-4 mb-2 bg-gradient-to-br from-red-950/30 to-transparent rounded-xl border border-red-900/20">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center ring-4 ring-red-500/20 overflow-hidden">
                            {userProfile?.photoURL || user?.photoURL ? (
                              <Image
                                src={userProfile?.photoURL || user?.photoURL || ''}
                                alt="Profile"
                                width={48}
                                height={48}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-lg font-bold text-white">
                                {displayInitial}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {userProfile?.displayName && userProfile?.username ? (
                              <>
                                <p className="text-base font-semibold text-white truncate">
                                  {userProfile.displayName}
                                </p>
                                <p className="text-xs text-gray-400 truncate">
                                  @{userProfile.username}
                                </p>
                              </>
                            ) : (
                              <p className="text-base font-semibold text-white truncate">
                                {userProfile?.username || user?.email?.split('@')[0] || 'User'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="space-y-1">
                        <DropdownMenuItem
                          className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-red-600/20 cursor-pointer transition-all rounded-lg group"
                          onClick={() => router.push('/dashboard')}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-red-600/10 group-hover:bg-red-600/20 flex items-center justify-center transition-colors">
                              <Film className="h-4 w-4 text-red-400" />
                            </div>
                            <span className="font-medium">My Watchlist</span>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer transition-all rounded-lg group"
                          onClick={() => router.push('/profile')}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white/5 group-hover:bg-white/10 flex items-center justify-center transition-colors">
                              <User className="h-4 w-4 text-gray-400 group-hover:text-white" />
                            </div>
                            <span className="font-medium">Profile</span>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer transition-all rounded-lg group"
                          onClick={() => router.push('/settings')}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white/5 group-hover:bg-white/10 flex items-center justify-center transition-colors">
                              <Settings className="h-4 w-4 text-gray-400 group-hover:text-white" />
                            </div>
                            <span className="font-medium">Settings</span>
                          </div>
                        </DropdownMenuItem>
                        {isAdmin && (
                          <DropdownMenuItem
                            className="px-3 py-2.5 text-gray-300 hover:text-white hover:bg-purple-600/20 cursor-pointer transition-all rounded-lg group"
                            onClick={() => window.open('/admin', '_blank')}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-purple-600/10 group-hover:bg-purple-600/20 flex items-center justify-center transition-colors">
                                <Shield className="h-4 w-4 text-purple-400" />
                              </div>
                              <span className="font-medium">Admin Panel</span>
                            </div>
                          </DropdownMenuItem>
                        )}
                      </div>

                      <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-2"></div>

                      {/* Logout section */}
                      <DropdownMenuItem
                        className="px-3 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-600/20 cursor-pointer transition-all rounded-lg group"
                        onClick={handleLogout}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-red-600/10 group-hover:bg-red-600/20 flex items-center justify-center transition-colors">
                            <LogOut className="h-4 w-4" />
                          </div>
                          <span className="font-medium">Logout</span>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  {/* Sign in/up buttons for unauthenticated users */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => router.push('/auth/signin')}
                    className="text-gray-300 hover:text-white hover:bg-white/10"
                    suppressHydrationWarning
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => router.push('/auth/signup')}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    suppressHydrationWarning
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search Dialog */}
      <SearchDialog open={showSearchDialog} onClose={() => setShowSearchDialog(false)} />
    </>
  );
}